import React from 'react';
import './App.css';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import BusinessDetailsPage from './pages/BusinessDetailsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HomePage from './pages/HomePage';
import SearchResultsPage from './pages/SearchResultsPage';
import TermsPage from './pages/TermsPage';
import AboutCheestaPage from './pages/AboutCheestaPage';
import HelpPage from './pages/HelpPage';
import ContactPage from './pages/ContactPage';
import CheestaServicesPage from './pages/CheestaServicesPage';
import HireUsPage from './pages/HireUsPage';
import FreeConsultationPage from './pages/FreeConsultationPage';
import NotFoundPage from './pages/NotFoundPage';
import ListingCheckPage from './pages/ListingCheckPage';
import ScrollToTop from './components/common/ScrollToTop';
import AnalyticsTracker from './components/common/AnalyticsTracker';
import SessionInterceptor from './components/common/SessionInterceptor';

// Signed-in / admin screens are loaded on demand so visitors (and Google's
// mobile crawler) landing on the public pages don't download the registration
// form, admin dashboard, rich-text editor etc. up front.
// If a chunk is missing (the site was redeployed while this tab was open),
// reload once to pick up the new build instead of showing a blank page.
const lazyPage = (load: () => Promise<{ default: React.ComponentType<any> }>) =>
  React.lazy(() =>
    load().catch((err) => {
      const key = 'cheesta-chunk-reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise<{ default: React.ComponentType<any> }>(() => {});
      }
      throw err;
    })
  );

const RegisterBusinessPage = lazyPage(() => import('./pages/RegisterBusinessPage'));
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const AdminDashboardPage = lazyPage(() => import('./pages/AdminDashboardPage'));
const PreviewPage = lazyPage(() => import('./pages/PreviewPage'));
const BusinessPerformancePage = lazyPage(() => import('./pages/BusinessPerformancePage'));
const UserPerformanceListPage = lazyPage(() => import('./pages/UserPerformanceListPage'));
const ProfileSettingsPage = lazyPage(() => import('./pages/ProfileSettingsPage'));
const LoginActivityPage = lazyPage(() => import('./pages/LoginActivityPage'));
const WebsiteScanPage = lazyPage(() => import('./pages/WebsiteScanPage'));
const ScannedWebsitesPage = lazyPage(() => import('./pages/ScannedWebsitesPage'));
const PromoteBusinessPage = lazyPage(() => import('./pages/PromoteBusinessPage'));
const FeaturedDonePage = lazyPage(() => import('./pages/FeaturedDonePage'));
const PromotionsHistoryPage = lazyPage(() => import('./pages/PromotionsHistoryPage'));
const SiteHealthPage = lazyPage(() => import('./pages/SiteHealthPage'));
const PricingPage = lazyPage(() => import('./pages/PricingPage'));

function App() {
  return (
    <SessionInterceptor>
      <div className="App">
        <ScrollToTop />
        <AnalyticsTracker />
        <Header />


      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/search" element={<SearchResultsPage />} />
        {/* Readable browse addresses. nginx.conf's route list and the Caddyfile must know these too. */}
        <Route path="/category/:categorySlug" element={<SearchResultsPage />} />
        <Route path="/city/:citySlug" element={<SearchResultsPage />} />
        <Route path="/business/register" element={<RegisterBusinessPage />} />
        <Route path="/user/business/register" element={<RegisterBusinessPage />} />
        <Route path="/user/business/edit/:id" element={<RegisterBusinessPage />} />
        <Route path="/user/business/performance/:id" element={<BusinessPerformancePage />} />
        <Route path="/user/business/:id/promote" element={<PromoteBusinessPage />} />
        <Route path="/user/featured/done" element={<FeaturedDonePage />} />
        <Route path="/user/promotions" element={<PromotionsHistoryPage />} />
        <Route path="/user/security" element={<SiteHealthPage />} />
        <Route path="/user/performance" element={<UserPerformanceListPage />} />
        <Route path="/user/settings" element={<ProfileSettingsPage />} />
        <Route path="/user/login-activity" element={<LoginActivityPage />} />
        <Route path="/user/website-scan" element={<WebsiteScanPage />} />
        <Route path="/user/scanned-websites" element={<ScannedWebsitesPage />} />
        <Route path="/business/:id" element={<BusinessDetailsPage />} />
        <Route path="/listing-check/:token" element={<ListingCheckPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/user" element={<DashboardPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/preview" element={<PreviewPage />} />
        <Route path="/cheesta-terms" element={<TermsPage />} />
        <Route path="/cheesta-help" element={<HelpPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/cheesta-services" element={<CheestaServicesPage />} />
        <Route path="/hire-us" element={<HireUsPage />} />
        <Route path="/free-consultation" element={<FreeConsultationPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about-cheesta-iranain-australia" element={<AboutCheestaPage />} />
        {/* Keep last: any unknown address. nginx.conf's route list must match these paths. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </React.Suspense>

      <Footer />
      </div>
    </SessionInterceptor>
  );
}


export default App;